import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import { SpeedUnit } from '../../types';

interface SpeedometerProps {
  speed: number;
  speedUnit: SpeedUnit;
  acceleration: number;
  maxSpeed?: number;
}

export const EvSportSpeedometer: React.FC<SpeedometerProps> = ({
  speed,
  speedUnit,
  acceleration,
  maxSpeed = 260,
}) => {
  const hyperRingRef = useRef<SVGCircleElement | null>(null);
  const raysRef = useRef<SVGGElement | null>(null);
  const speedTextRef = useRef<HTMLSpanElement | null>(null);
  const prevSpeedRef = useRef<number>(0);

  const clampedSpeed = Math.max(0, Math.min(maxSpeed, speed));
  const speedRatio = clampedSpeed / maxSpeed;
  const radius = 125;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - speedRatio * 0.85);

  useEffect(() => {
    if (!speedTextRef.current) return;
    const obj = { val: prevSpeedRef.current };
    anime({
      targets: obj,
      val: speed,
      round: 1,
      duration: 300,
      easing: 'easeOutExpo',
      update: () => {
        if (speedTextRef.current) {
          speedTextRef.current.textContent = String(Math.round(obj.val));
        }
      },
    });
    prevSpeedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    if (!hyperRingRef.current) return;
    anime({
      targets: hyperRingRef.current,
      strokeDashoffset,
      duration: 350,
      easing: 'easeOutQuad',
    });
  }, [strokeDashoffset]);

  useEffect(() => {
    if (!raysRef.current) return;
    anime({
      targets: raysRef.current,
      rotate: '+=360deg',
      duration: Math.max(1200, 6000 - clampedSpeed * 30),
      easing: 'linear',
      loop: true,
    });
  }, [clampedSpeed]);

  const isHardThrust = acceleration > 0.3;

  return (
    <div className="relative w-80 h-80 md:w-92 md:h-92 flex flex-col items-center justify-center select-none">
      {/* Hyper-Drive Pulsing Aura */}
      <div
        className={`absolute inset-0 rounded-full blur-3xl transition-all duration-200 pointer-events-none ${
          isHardThrust ? 'bg-rose-500/35 scale-110' : 'bg-fuchsia-600/20'
        }`}
      />

      {/* Futuristic Segmented Hyper-Ring & Rotating Rays */}
      <svg className="absolute inset-0 w-full h-full transform -rotate-140" viewBox="0 0 300 300">
        {/* Background Segment Track */}
        <circle
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="rgba(217, 70, 239, 0.15)"
          strokeWidth="6"
          strokeDasharray="6 4"
        />

        {/* Dynamic Hyper-Drive Velocity Arc */}
        <circle
          ref={hyperRingRef}
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="#e879f9"
          strokeWidth="7"
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeLinecap="round"
          className="filter drop-shadow-[0_0_14px_rgba(232,121,249,0.8)]"
        />

        {/* Radial Stator Rays */}
        <g ref={raysRef} style={{ transformOrigin: '150px 150px' }}>
          {Array.from({ length: 12 }, (_, i) => {
            const angle = (i * 30 * Math.PI) / 180;
            const x1 = 150 + 134 * Math.cos(angle);
            const y1 = 150 + 134 * Math.sin(angle);
            const x2 = 150 + 144 * Math.cos(angle);
            const y2 = 150 + 144 * Math.sin(angle);
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="rgba(244, 114, 182, 0.6)"
                strokeWidth="2"
                strokeLinecap="round"
              />
            );
          })}
        </g>
      </svg>

      {/* Center Dynamic Hyper-Drive Readout */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-fuchsia-950/80 border border-fuchsia-500/60 mb-1">
          <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-ping" />
          <span className="text-[10px] font-mono tracking-[0.25em] text-fuchsia-300 font-black uppercase">
            HYPER-DRIVE FIELD
          </span>
        </div>

        <span
          ref={speedTextRef}
          className="text-7xl md:text-8xl font-black font-display text-white tracking-tighter drop-shadow-[0_4px_24px_rgba(232,121,249,0.5)] my-1"
        >
          {Math.round(speed)}
        </span>

        <span className="text-xs font-mono tracking-widest text-fuchsia-400 font-black uppercase">
          {speedUnit.toUpperCase()}
        </span>

        {/* Dynamic Plasma Thrust Bar */}
        <div className="w-28 h-1.5 bg-slate-900 rounded-full overflow-hidden mt-3 border border-fuchsia-900/60">
          <div
            className="h-full bg-gradient-to-r from-fuchsia-500 to-rose-400 rounded-full transition-all duration-150 shadow-[0_0_8px_rgba(232,121,249,0.8)]"
            style={{ width: `${Math.max(10, Math.min(100, (acceleration + 0.5) * 100))}%` }}
          />
        </div>
      </div>
    </div>
  );
};
